import clsx from 'clsx';
import type { ReactNode } from 'react';

import type { CreditAmount, PaymentDoc, StripeSubscription, UserAccountDoc } from '@flintmint/core-shared/firestore/schemas';
import {
  computeTier,
  creditsLeftPercent,
  effectiveMonthlyQuota,
  formatCount,
  formatTypicalNotes,
  hasCreditsLeft,
  isCreditAmount,
  pluralNoun,
  wholeCreditsLeft,
} from '@flintmint/core-shared/utils';
import { Button } from '@flintmint/web-shared/components/button';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';

import { BatteryIcon, RechargeIcon } from '@/features/llm-chat/energy/battery-icon';
import { creditedJouleBalance, getEnergyView, rechargeStatus } from '@/features/llm-chat/energy/energy-view';
import { useAppSettings } from '@/hooks/use-app-settings';
import { usePayments } from '@/hooks/use-payments';
import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { urlOverlays } from '@/constants/url-overlays';
import { useUserAccount } from '@/hooks/use-user-account';

import {
  canBuyRecharge,
  canCancelPendingSubscription,
  canCancelSubscription,
  canChoosePlan,
  canOpenPortal,
  canReactivateSubscription,
  canStartPlanEarly,
  heldChargeUrl,
  isPendingFirstCharge,
  isTrialEligible,
} from '../billing-state';
import InfinityIcon from '../assets/infinity.svg?react';
import { CancelSubscription } from '../cancel-subscription';
import { CheckoutSuccess } from '../checkout-success';
import {
  DAY_MS,
  formatBillingDate,
  formatBillingDayMonth,
  formatJoules,
  formatPrice,
  formatPriceWithCents,
  intervalLabels,
  pendingFirstPaymentCopy,
} from '../billing-format';
import { type SkeletonLineStyle, Text, TextSkeleton, type TextStyle } from '../billing-text';
import { RechargeSuccess } from '../recharge-success';
import { useOpenPortal } from '../use-open-portal';
import { useUndoCancelSubscription } from '../use-cancel-subscription';

import styles from './subscription-billing.module.scss';

export const SubscriptionBilling = () => {
  const { account, loading } = useUserAccount();

  return (
    <div className={styles.subscriptionBilling} data-testid="settings-billing">
      <div className={styles.heading}>
        <h2>Subscription & Billing</h2>
        <p>Manage your subscription and payment details.</p>
      </div>

      <BillingStatus account={account} loading={loading} />
      <CheckoutSuccess />
      <RechargeSuccess />
      <CancelSubscription />
    </div>
  );
};

/**
 * While the account loads, the Free Trial-available view (the most common state) renders with every text
 * and button replaced in place by a skeleton of its size, so a Free account loads with no layout shift.
 */
const BillingStatus = ({ account, loading }: { account: UserAccountDoc | undefined; loading: boolean }) => {
  const { openPortal, opening } = useOpenPortal();
  const { open: openPlanPicker } = useUrlOverlay(urlOverlays.planPicker, urlOverlays);
  const { open: openRecharge } = useUrlOverlay(urlOverlays.recharge, urlOverlays);
  const { open: openStartPlan } = useUrlOverlay(urlOverlays.startPlan, urlOverlays);
  const { open: openCancelSubscription } = useUrlOverlay(urlOverlays.cancelSubscription, urlOverlays);
  const { open: openCancelPendingSubscription } = useUrlOverlay(urlOverlays.cancelPendingSubscription, urlOverlays);
  const { undoCancelSubscription, isPending: reactivating } = useUndoCancelSubscription();
  const view = loading ? trialAvailableView : getBillingView(account);
  const showPortal = canOpenPortal(account);
  const held = !!heldChargeUrl(account);
  const openPayment = () => {
    if (held) openStartPlan();
    else void openPortal();
  };
  const openCancel = canCancelSubscription(account)
    ? openCancelSubscription
    : canCancelPendingSubscription(account)
    ? openCancelPendingSubscription
    : undefined;
  const reactivatePlan = canReactivateSubscription(account) ? account?.stripeSubscription?.planName : undefined;
  const reactivateButton = reactivatePlan !== undefined && (
    <Button size="big" cornerRadius="smooth" loading={reactivating} onClick={undoCancelSubscription}>
      {`Reactivate ${reactivatePlan}`}
    </Button>
  );
  const startPlanButton = !loading && canStartPlanEarly(account) && (
    <Button size="big" cornerRadius="smooth" onClick={openStartPlan}>
      Start subscription now
    </Button>
  );
  const plansLabel = view.plansLabel ?? 'Compare plans';
  const plansAction = loading ? (
    <ButtonSkeleton label={plansLabel} />
  ) : (
    canChoosePlan(account) && (
      <Button size="big" cornerRadius="smooth" color={showPortal ? 'grey' : 'hero'} onClick={openPlanPicker}>
        {plansLabel}
      </Button>
    )
  );

  const rechargeButton = !loading && canBuyRecharge(account) && (
    <Button size="big" cornerRadius="smooth" color="grey" onClick={openRecharge}>
      Recharge
    </Button>
  );

  return (
    <>
      <StatusBanner {...view.banner} loading={loading} />

      <section
        className={clsx(styles.planCard, { [styles.compact]: view.compactCards })}
        aria-labelledby="billing-plan-name"
        aria-hidden={loading || undefined}
      >
        <h3 id="billing-plan-name">
          <Text style={textStyles.cardTitle} loading={loading}>
            {view.title}
          </Text>
        </h3>
        <dl>
          {view.details.map(({ label, value }) => (
            <div key={label}>
              <dt>
                <Text style={textStyles.caption} loading={loading}>
                  {label}
                </Text>
              </dt>
              <dd>
                <Text style={textStyles.value} loading={loading}>
                  {value}
                </Text>
              </dd>
            </div>
          ))}
        </dl>
        {view.description && (
          <div className={styles.description}>
            <Text style={textStyles.caption} loading={loading}>
              {view.description}
            </Text>
          </div>
        )}
        {(showPortal || startPlanButton || (plansAction && !view.plansInEnergyCard)) && (
          <div className={styles.actions}>
            {reactivateButton}
            {startPlanButton}
            {showPortal && (
              <Button
                size="big"
                cornerRadius="smooth"
                color={reactivateButton || startPlanButton ? 'grey' : 'hero'}
                loading={opening}
                onClick={openPayment}
              >
                {view.portalLabel ?? 'Payment details & invoices'}
              </Button>
            )}
            {!view.plansInEnergyCard && plansAction}
            {openCancel && (
              <Button
                appearance="clear"
                cornerRadius="smooth"
                className={styles.cancelLink}
                disabled={opening}
                onClick={openCancel}
              >
                {view.cancelLabel ?? 'Cancel subscription'}
              </Button>
            )}
          </div>
        )}
      </section>

      {view.energy && (
        <EnergyCard
          account={account}
          energy={view.energy}
          action={rechargeButton || (view.plansInEnergyCard && plansAction)}
          compact={view.compactCards}
          loading={loading}
        />
      )}

      {!loading && <PaymentHistory />}
    </>
  );
};

const textStyles = {
  bannerTitle: { lineHeight: 16, textHeight: 12, charWidth: 7.6 },
  bannerMessage: { lineHeight: 22, textHeight: 10, charWidth: 6.7 },
  cardTitle: { lineHeight: 36, textHeight: 18, charWidth: 12.9 },
  caption: { lineHeight: 18, textHeight: 8, charWidth: 5.7 },
  value: { lineHeight: 24, textHeight: 12, charWidth: 8 },
  allowanceName: { lineHeight: 20, textHeight: 10, charWidth: 7 },
  allowanceFigure: { lineHeight: 40, textHeight: 24 },
  note: { lineHeight: 20, textHeight: 10, charWidth: 6.4 },
} satisfies Record<string, TextStyle | SkeletonLineStyle>;

/**
 * The Joules line's loading width: "1,000 / 1,000 Joules · About 50 typical notes left · One-time allowance." at
 * 13px renders 433 (measured).
 */
const allowanceLineWidth = 433;

/** The remaining-Energy figure's loading width: the widest figure, "100%", at 32px semibold (measured 20.5 a glyph). */
const allowanceFigureWidth = 82;

/** A `size="big"` Button's box: 46 tall, its label (16px medium, about 8.8 per glyph) plus 24 padding on each side wide. */
const ButtonSkeleton = ({ label }: { label: string }) => (
  <span data-testid="billing-button-skeleton">
    <Skeleton height={46} trueWidth={Math.round(label.length * 8.8) + 48} borderRadius={12} />
  </span>
);

/**
 * Hidden until the payments load, and when there are none: the design has no empty state. $0 payments are left out:
 * no money was paid.
 */
const PaymentHistory = () => {
  const payments = usePayments()?.filter((payment) => payment.amountCents > 0);
  if (!payments?.length) return null;

  return (
    <section className={styles.paymentHistory} aria-labelledby="billing-payment-history-title">
      <h3 id="billing-payment-history-title">Payment history</h3>
      <table>
        <thead>
          <tr>
            <th>Payment</th>
            <th>Date</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id}>
              <td>{paymentLabels[payment.type]}</td>
              <td>{formatBillingDate(payment.paidAt)}</td>
              <td>{`${formatPriceWithCents(payment.amountCents)} USD`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const paymentLabels: Record<PaymentDoc['type'], string> = { subscription: 'Subscription', topUp: 'Recharge' };

type BillingView = {
  banner: StatusBannerProps;
  title: string;
  details: { label: string; value: string }[];
  description?: string;
  portalLabel?: 'Update payment method' | 'Complete payment';
  cancelLabel?: 'Cancel Trial' | 'Cancel pending subscription' | 'Cancel scheduled subscription';
  plansLabel?: 'Explore paid plans';
  /** The plans link sits in the Energy card header instead of the plan card. */
  plansInEnergyCard?: true;
  /** The plan and Energy cards' tighter spacing and smaller titles (active Trial and paid states). */
  compactCards?: true;
  /** The Energy card's allowance. */
  energy?: EnergyAllowance;
};

/**
 * The one-time AI Preview quota, the comped Unlimited, or the monthly quota under `name`, its
 * Joules line ending in `detail` when given, followed by `notes`.
 */
type EnergyAllowance =
  | { kind: 'introductory'; note?: string }
  | { kind: 'unlimited' }
  | { kind: 'monthly'; name: 'Monthly Energy' | 'Trial Energy'; trial?: true; detail?: string; notes: string[] };

/** One view per design state; `computeTier` decides comped, paid, or Free. */
const getBillingView = (account: UserAccountDoc | undefined): BillingView => {
  const tier = computeTier(account);
  const subscription = account?.stripeSubscription;

  if (tier === 'comped') return sponsoredView;
  if (tier === 'paid' && subscription) return getPaidView(subscription);
  if (!account || isTrialEligible(account)) return trialAvailableView;
  if (!subscription) return getFreeView(account);
  if (isPendingFirstCharge(subscription)) return getPendingView(subscription);
  if (subscription.status === 'incomplete_expired') return getPaymentExpiredView(account);
  return subscription.hasPaidInvoice ? getFreeView(account) : getTrialEndedView(account);
};

// Comps are never tied to another plan, so the card is always Basic.
const sponsoredView: BillingView = {
  banner: {
    tone: 'neutral',
    title: 'Your Basic access is sponsored',
    message: 'Your sponsored account includes Basic features. No subscription payment is scheduled.',
  },
  title: 'Basic',
  details: [
    { label: 'Subscription status', value: 'Sponsored' },
    { label: 'Billing cycle', value: 'Covered' },
    { label: 'Subscription payment', value: 'Not scheduled' },
  ],
  description: 'All paid features included',
  energy: { kind: 'unlimited' },
};

const getPaidView = (subscription: StripeSubscription): BillingView => {
  // A scheduled end, set in the app or the Stripe dashboard, can differ from the period end.
  const endsOrRenewsAt = subscription.cancelAt ?? subscription.currentPeriodEnd;
  const date = formatBillingDate(endsOrRenewsAt);
  // The active Trial's description leaves out the year inside the sentence; the Trial-renewal-off banner and the scheduled cancel keep it.
  const dayMonth = formatBillingDayMonth(endsOrRenewsAt);
  // The subscription's synced price, before tax.
  const amount = formatPrice(subscription.amountCents);
  const interval = intervalLabels[subscription.interval];
  const billingCycle = `${interval} · ${amount}`;

  if (subscription.status === 'past_due') {
    return {
      banner: {
        tone: 'warning',
        title: 'Your payment failed',
        message: 'AI use is limited until your payment is fixed. Update your payment method to restore full access.',
      },
      title: subscription.planName,
      details: [
        { label: 'Subscription status', value: 'Payment overdue' },
        { label: 'Billing cycle', value: billingCycle },
        { label: 'Payment status', value: 'Awaiting payment' },
        ...(subscription.cancelAt === undefined ? [] : [{ label: 'Paid access until', value: date }]),
      ],
      portalLabel: 'Update payment method',
      compactCards: true,
      // The failed renewal state shows only Overdraft, which the backend does not have: the monthly quota against the full month.
      // The card counts Joules against the full month, not the grace share the AI gate allows while past_due:
      // `settings/system.gracePeriodQuotaPercent` is admin-only, an accepted gap.
      energy: { kind: 'monthly', name: 'Monthly Energy', notes: [] },
    };
  }

  // During a Trial the monthly quota holds the Trial's one-time allowance (the subscription sync grants
  // `trialCredits` and starts no quota month until the Trial ends).
  const trialEnergy: EnergyAllowance = {
    kind: 'monthly',
    name: 'Trial Energy',
    trial: true,
    notes: [`One-time Trial allowance. Ends ${date}; it does not reset.`],
  };

  // The held invoice's expiry is not stored, so the card has no "Complete payment by" description.
  if (subscription.status === 'trialing' && subscription.pendingInvoiceUrl) {
    return {
      banner: {
        tone: 'neutral',
        title: 'Your early start is on hold',
        message: `Your Trial and remaining Energy stay available until ${formatBillingDate(subscription.currentPeriodEnd)}.`,
      },
      title: 'Trial',
      details: [
        { label: 'Current access', value: 'Trial' },
        { label: 'Selected plan', value: subscription.planName },
        { label: 'Billing cycle', value: interval },
        { label: 'Payment', value: 'Payment held' },
      ],
      portalLabel: 'Complete payment',
      cancelLabel: 'Cancel scheduled subscription',
      energy: trialEnergy,
    };
  }

  if (subscription.status === 'trialing' && subscription.cancelAt !== undefined) {
    return {
      banner: {
        tone: 'neutral',
        title: 'Your scheduled subscription is cancelled',
        message: `You will not be charged. Your remaining Trial stays available until ${date}.`,
      },
      title: 'Trial',
      details: [
        { label: 'Current access', value: 'Trial' },
        { label: 'Automatic subscription', value: 'Cancelled' },
        { label: 'Trial ends', value: date },
      ],
      description: `No payment is scheduled. Use any remaining Trial Energy until ${dayMonth}, then continue on Free.`,
      energy: trialEnergy,
    };
  }

  if (subscription.status === 'trialing') {
    return {
      banner: {
        tone: 'neutral',
        title: `Your ${trialDays(subscription)}-day Trial is active`,
        message: `Your selected plan starts on ${dayMonth}. Cancel before then to avoid the charge.`,
      },
      title: 'Trial',
      details: [
        { label: 'Subscription status', value: 'Trial' },
        { label: 'After Trial', value: subscription.planName },
        { label: 'First payment', value: `${amount} on ${date}` },
      ],
      description: `First charge ${date}: ${amount}/${subscription.interval}. Cancel Trial before it ends to avoid payment.`,
      cancelLabel: 'Cancel Trial',
      compactCards: true,
      energy: trialEnergy,
    };
  }

  if (subscription.cancelAt !== undefined) {
    return {
      banner: {
        tone: 'neutral',
        title: 'Your subscription has been cancelled',
        message: `Paid access continues until ${date}. After that, you’ll move to Free.`,
      },
      title: subscription.planName,
      details: [
        { label: 'Subscription status', value: 'Cancelled' },
        { label: 'Paid access until', value: date },
        { label: 'Next plan', value: 'Free' },
      ],
      compactCards: true,
      energy: {
        kind: 'monthly',
        name: 'Monthly Energy',
        detail: `Available until ${date}.`,
        notes: [
          subscription.interval === 'year'
            ? 'Resets monthly until then. Unused Monthly Energy does not roll over.'
            : 'Your monthly Energy will not renew.',
        ],
      },
    };
  }

  return {
    banner: {
      tone: 'neutral',
      title: 'Your subscription is active',
      message: 'Your selected paid plan is active. Manage your payment details below.',
    },
    title: subscription.planName,
    details: [
      { label: 'Subscription status', value: 'Active' },
      { label: 'Billing cycle', value: billingCycle },
      { label: 'Next payment', value: date },
    ],
    compactCards: true,
    energy: {
      kind: 'monthly',
      name: 'Monthly Energy',
      notes: ['Resets monthly. Unused Monthly Energy does not roll over.'],
    },
  };
};

/** A Trial's own length in days: during a Trial the subscription's period is its trial window. */
const trialDays = ({ currentPeriodStart, currentPeriodEnd }: StripeSubscription) =>
  Math.round((Date.parse(currentPeriodEnd) - Date.parse(currentPeriodStart)) / DAY_MS);

/** For a pending first charge (`isPendingFirstCharge`). */
const getPendingView = (subscription: StripeSubscription): BillingView => ({
  banner: {
    tone: 'neutral',
    ...pendingFirstPaymentCopy,
  },
  title: 'Subscription pending',
  details: [
    { label: 'Current access', value: 'Free' },
    { label: 'Selected plan', value: subscription.planName },
    { label: 'Billing cycle', value: intervalLabels[subscription.interval] },
    { label: 'Payment', value: 'Retry pending' },
  ],
  description: 'Your payment will be retried. You can cancel the pending subscription.',
  portalLabel: 'Update payment method',
  cancelLabel: 'Cancel pending subscription',
  energy: { kind: 'introductory' },
});

/** For a Trial-eligible account (`isTrialEligible`). */
const trialAvailableView: BillingView = {
  banner: {
    tone: 'neutral',
    title: 'Start a trial whenever you’re ready',
    message: 'Explore AI with a Trial. Choose your future plan when you are ready.',
  },
  title: 'Free',
  details: [
    { label: 'Subscription status', value: 'Free' },
    { label: 'Monthly cost', value: 'Free' },
    { label: 'Trial', value: 'Available' },
  ],
  description: 'Keep working with manual notes, Blocks and your existing content. Explore Trial when you need more AI.',
  energy: { kind: 'introductory', note: 'Continue on Free, or explore Trial for more AI.' },
  plansInEnergyCard: true,
};

const getFreeView = (account: UserAccountDoc): BillingView => ({
  banner: {
    tone: 'neutral',
    title: 'You’re now on Free',
    message: 'No subscription is active. Your account and existing content remain available.',
  },
  title: 'Free',
  details: [
    { label: 'Subscription status', value: 'Free' },
    { label: 'Monthly cost', value: 'Free' },
    { label: 'AI access', value: getFreeAiAccess(account) },
  ],
  description: 'Your permitted non-AI features remain available.',
  plansLabel: 'Explore paid plans',
  energy: { kind: 'introductory', note: 'Monthly Energy is not included without an eligible paid subscription.' },
});

// TODO: this copy is ours until design provides this state.
/** For a subscription whose first payment never completed before Stripe expired it (`incomplete_expired`): no Trial, no paid period. */
const getPaymentExpiredView = (account: UserAccountDoc): BillingView => ({
  banner: {
    tone: 'neutral',
    title: 'You’re on Free',
    message: 'Your last payment didn’t go through, so no plan started. Keep working on Free, or choose a plan when you’re ready.',
  },
  title: 'Free',
  details: [
    { label: 'Subscription status', value: 'Free' },
    { label: 'Monthly cost', value: 'Free' },
    { label: 'AI access', value: getFreeAiAccess(account) },
  ],
  energy: { kind: 'introductory', note: 'A failed payment does not add any Energy.' },
});

const getTrialEndedView = (account: UserAccountDoc): BillingView => ({
  banner: {
    tone: 'neutral',
    title: 'Your Trial has ended',
    message: 'No subscription started and no payment was taken. You can keep using Free.',
  },
  title: 'Free',
  details: [
    { label: 'Subscription status', value: 'Free' },
    { label: 'Trial', value: 'Ended' },
    { label: 'AI access', value: getFreeAiAccess(account) },
  ],
  description: 'Keep your existing content and manual tools, or choose a paid plan when you are ready.',
  energy: { kind: 'introductory', note: 'No new allowance is added.' },
});

const getFreeAiAccess = (account: UserAccountDoc) => (hasAiPreviewLeft(account) ? 'Introductory allowance' : 'Not included');

/** Free AI draws on the one-time AI Preview quota while a whole Joule is left (the backend AI gate). */
const hasAiPreviewLeft = (account: UserAccountDoc | undefined) => hasCreditsLeft(account?.aiPreviewQuota);

type StatusBannerProps = { tone: 'neutral' | 'warning'; title: string; message: string };

const StatusBanner = ({ tone, title, message, loading }: StatusBannerProps & { loading: boolean }) => (
  <div
    role={tone === 'warning' ? 'alert' : 'status'}
    aria-hidden={loading || undefined}
    className={clsx(styles.banner, { [styles.warning]: tone === 'warning' })}
  >
    <div className={styles.bannerTitle}>
      {loading ? (
        <Skeleton trueWidth={20} trueHeight={20} borderRadius={10} />
      ) : (
        <Icon type={tone === 'warning' ? 'alert-02-solid' : 'information-circle-solid'} size={20} className={styles.bannerIcon} />
      )}
      <Text style={textStyles.bannerTitle} loading={loading}>
        {title}
      </Text>
    </div>
    <div>
      <Text style={textStyles.bannerMessage} loading={loading}>
        {message}
      </Text>
    </div>
  </div>
);

type EnergyCardProps = {
  account: UserAccountDoc | undefined;
  energy: EnergyAllowance;
  action: ReactNode;
  compact: boolean | undefined;
  loading: boolean;
};

const EnergyCard = ({ account, energy, action, compact, loading }: EnergyCardProps) => {
  const jouleBalance = creditedJouleBalance(account);
  // The Joule balance sits in a column beside Monthly Energy; below Introductory and Trial Energy it keeps its meter.
  const rechargeBeside = energy.kind === 'monthly' && !energy.trial && account && jouleBalance;

  return (
    <section
      className={clsx(styles.energyCard, { [styles.compact]: compact })}
      aria-labelledby="billing-energy-title"
      aria-hidden={loading || undefined}
    >
      <div className={styles.energyHeader}>
        <h3 id="billing-energy-title">
          <Text style={textStyles.cardTitle} loading={loading}>
            Energy
          </Text>
        </h3>
        {action}
      </div>
      {energy.kind === 'unlimited' ? (
        <div className={styles.allowance}>
          <p className={clsx(styles.allowanceName, styles.unlimitedName)}>
            <BatteryIcon level="unlimited" size={20} />
            Monthly Energy
          </p>
          <div className={styles.unlimitedValue}>
            <InfinityIcon aria-hidden />
            <p>Unlimited</p>
          </div>
        </div>
      ) : energy.kind === 'introductory' ? (
        <QuotaEnergy
          name="AI Preview"
          quota={account?.aiPreviewQuota}
          detail={hasAiPreviewLeft(account) ? 'One-time allowance.' : 'One-time allowance used up.'}
          notes={energy.note === undefined ? [] : [energy.note]}
          loading={loading}
        />
      ) : rechargeBeside ? (
        <div className={styles.balances}>
          <QuotaEnergy
            name={energy.name}
            quota={effectiveMonthlyQuota(account, new Date())}
            detail={energy.detail}
            notes={energy.notes}
            estimateAsSentence
            loading={loading}
          />
          <div className={styles.balanceSeparator} />
          <RechargeBalance account={account} balance={jouleBalance} />
        </div>
      ) : (
        <QuotaEnergy
          name={energy.name}
          quota={effectiveMonthlyQuota(account, new Date())}
          detail={energy.detail}
          notes={energy.notes}
          loading={loading}
        />
      )}
      {energy.kind !== 'unlimited' && jouleBalance && !rechargeBeside && (
        <QuotaEnergy
          name="Recharge"
          icon={<RechargeIcon size={20} className={styles.allowanceIcon} />}
          quota={jouleBalance}
          detail={undefined}
          notes={[]}
          loading={loading}
        />
      )}
    </section>
  );
};

type QuotaEnergyProps = {
  name: string;
  /** Defaults to the full battery. */
  icon?: ReactNode;
  quota: CreditAmount | undefined;
  detail: string | undefined;
  notes: string[];
  /** "About <n> typical notes." instead of "About <n> typical notes left", as beside the Joule balance. */
  estimateAsSentence?: true;
  loading: boolean;
};

/**
 * A quota's remaining share, meter and "<left> / <total> Joules · About <n> typical notes left · <detail>"
 * line. With no quota on the account it counts as empty, and only the detail is known; the estimate is
 * left out while the app settings load.
 */
const QuotaEnergy = ({ name, icon, quota, detail, notes, estimateAsSentence, loading }: QuotaEnergyProps) => {
  const remaining = creditsLeftPercent(quota);
  const creditsPerTypicalNote = useAppSettings()?.creditsPerTypicalNote;
  let joulesLine = detail;
  if (isCreditAmount(quota)) {
    const joulesLeft = wholeCreditsLeft(quota);
    const typicalNotes = formatTypicalNotes(joulesLeft, creditsPerTypicalNote);
    const estimate = estimateAsSentence ? typicalNotes : typicalNotes && `${typicalNotes} left`;
    joulesLine = formatJoules(joulesLeft, quota.total, estimate, detail);
    // The sentence form ends in a period, as a detail does.
    if (estimateAsSentence && estimate && !detail) joulesLine += '.';
  }

  return (
    <div className={styles.allowance}>
      <AllowanceName
        name={name}
        icon={icon ?? <Icon type="battery-full-solid" size={20} className={styles.allowanceIcon} aria-hidden />}
        loading={loading}
      />
      <AllowanceFigure remaining={remaining} loading={loading} />
      {loading ? (
        <Loader type="progress" thin />
      ) : (
        <Loader
          type="progress"
          progress={remaining}
          role="progressbar"
          aria-label={`${name} remaining`}
          aria-valuenow={remaining}
          aria-valuemin={0}
          aria-valuemax={100}
          thin
        />
      )}
      <div className={styles.allowanceNote} data-testid="energy-notes">
        {loading ? (
          <div>
            <TextSkeleton style={textStyles.note} width={allowanceLineWidth} />
          </div>
        ) : (
          joulesLine && <div>{joulesLine}</div>
        )}
        {notes.map((note) => (
          <div key={note}>
            <Text style={textStyles.note} loading={loading}>
              {note}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * The Joule balance's column beside Monthly Energy: its status and whole Joules left, without a meter. Frozen, a
 * Settings-only status, while a failed renewal payment holds Joules that are left. Only a loaded account holds a
 * Joule balance, so it has no loading layout.
 */
const RechargeBalance = ({ account, balance }: { account: UserAccountDoc; balance: CreditAmount }) => {
  const view = getEnergyView(account);
  const joulesLeft = wholeCreditsLeft(balance);
  const frozen = account.stripeSubscription?.status === 'past_due' && joulesLeft > 0;
  const status = frozen ? 'Frozen' : view && rechargeStatus(account, view);

  return (
    <div className={styles.allowance} data-testid="recharge-balance">
      <div className={styles.allowanceHeading}>
        <AllowanceName name="Recharge" icon={<RechargeIcon size={20} className={styles.allowanceIcon} />} loading={false} />
        {status && (
          <span className={styles.allowanceStatus} data-testid="recharge-status">
            {status}
          </span>
        )}
      </div>
      <div className={styles.allowanceValue}>
        <span>{formatCount(joulesLeft)}</span>
        <span>{pluralNoun(joulesLeft, 'Joule')}</span>
      </div>
      <div className={styles.allowanceNote} data-testid="recharge-notes">
        <div>{frozen ? 'Saved until your renewal payment succeeds.' : 'Used when Monthly Energy runs out.'}</div>
      </div>
    </div>
  );
};

const AllowanceName = ({ name, icon, loading }: { name: string; icon: ReactNode; loading: boolean }) => (
  <div className={styles.allowanceName}>
    {loading ? <Skeleton trueWidth={20} trueHeight={20} /> : icon}
    <Text style={textStyles.allowanceName} loading={loading}>
      {name}
    </Text>
  </div>
);

const AllowanceFigure = ({ remaining, loading }: { remaining: number; loading: boolean }) => (
  <div className={styles.allowanceValue}>
    <span>{loading ? <TextSkeleton style={textStyles.allowanceFigure} width={allowanceFigureWidth} /> : `${remaining}%`}</span>
    <span>
      <Text style={textStyles.note} loading={loading}>
        remaining
      </Text>
    </span>
  </div>
);
